'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import React, {
  useEffect, useId, useMemo, useState
} from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { Button } from '@/components/button/button';
import { Input } from '@/components/input/input';
import Modal from '@/components/modal/modal';
import { Heading } from '@/components/typography/heading';
import { Text } from '@/components/typography/text';
import { useGrantContractAccess } from '@/hooks/mutations/useContractAccessMutations';
import { useAgentsQuery } from '@/hooks/queries/useAgents';
import { BaseAgent } from '@/types/agent';

import styles from './ManageAccessSection.module.scss';
import { extractApiErrorMessage } from './utils';

type AddConstellationModalProps = {
  contractId: string;
  contractName?: string;
  // Constellation principals already connected to the contract — excluded
  // from the picker so the same constellation isn't added twice.
  existingConstellationPrincipals: Set<string>;
  isOpen: boolean;
  onClose: () => void;
};

const schema = z.object( {
  constellation: z.string().min( 1, 'Pick a constellation' )
} );
type FormData = z.infer<typeof schema>;

/**
 * Friendly name shown in the picker. aidp-core's /v2/agents returns rows
 * where each row IS a constellation (per Ryan Smith's 2026-06-18
 * #aidp-engineering thread — core's domain layer mis-names the column
 * `agent` but the FGA layer writes `constellation:{agent.name}`).
 */
const agentDisplayName = ( a: BaseAgent ): string => a.name ?? a.constellationName ?? a.id;

/**
 * FGA principal MUST be `constellation:{name}` to match the tuple core
 * writes. We refuse to construct a principal from `id` as a fallback:
 * `constellation:{id}` would NOT match the cascade tuple core wrote on
 * the constellation, so the grant request succeeds with HTTP 200 but
 * the cascade does nothing (and dedupe against existing principals
 * fails too because different IDs would collide once normalized).
 * Returns `null` for nameless agents; the picker filters them out.
 */
const agentToPrincipal = ( a: BaseAgent ): string | null => ( a.name ? `constellation:${ a.name }` : null );

const matchesQuery = ( a: BaseAgent, q: string ): boolean => {
  if ( !q ) return true;
  const needle = q.toLowerCase();
  return agentDisplayName( a ).toLowerCase().includes( needle );
};

export function AddConstellationModal( {
  contractId,
  contractName,
  existingConstellationPrincipals,
  isOpen,
  onClose
}: AddConstellationModalProps ) {
  const baseId = useId();
  // Fetch the full agent list once, filter client-side. Same pattern as
  // the user picker — fine for org sizes up to ~500 constellations. If
  // perf becomes an issue, swap for /v2/agents?q= server search.
  const {
    data: agents = [],
    isLoading: agentsLoading,
    isError: agentsError
  } = useAgentsQuery( { enabled: isOpen } );
  const grant = useGrantContractAccess( contractId );

  const [ query, setQuery ] = useState( '' );

  const { handleSubmit, setValue, watch, reset, formState } = useForm<FormData>( {
    resolver: zodResolver( schema ),
    defaultValues: { constellation: '' }
  } );
  const selectedPrincipal = watch( 'constellation' );

  // Pre-compute the (agent, principal) pair so we do the name-null guard
  // once and downstream code doesn't have to keep re-checking. Nameless
  // agents drop out here — they can't be granted access anyway (see
  // agentToPrincipal jsdoc).
  const candidates = useMemo( () => {
    return agents
      .map( ( a ) => ( { agent: a, principal: agentToPrincipal( a ) } ) )
      .filter( ( c ): c is { agent: BaseAgent; principal: string } => c.principal !== null )
      .filter( ( c ) => !existingConstellationPrincipals.has( c.principal ) )
      .filter( ( c ) => matchesQuery( c.agent, query ) )
      .slice( 0, 50 );
  }, [ agents, query, existingConstellationPrincipals ] );

  // Same drift guard as AddPrincipalModal: if the user keyboard-navigated
  // to a candidate then typed to filter it out, clear the form-held value
  // so Enter doesn't submit an invisible selection.
  useEffect( () => {
    if ( selectedPrincipal && !candidates.some( ( c ) => c.principal === selectedPrincipal ) ) {
      setValue( 'constellation', '', { shouldValidate: false } );
    }
  }, [ candidates, selectedPrincipal, setValue ] );

  const onSubmit = handleSubmit( async( data ) => {
    try {
      // No `permission` / `pii` for constellation grants — backend infers
      // `constellation:{name}#user_invoker` cascade per the FGA model.
      await grant.mutateAsync( { principal: data.constellation } );
      const name = data.constellation.replace( /^constellation:/, '' );
      toast.success( `Connected constellation: ${ name }` );
      reset();
      setQuery( '' );
      onClose();
    } catch( err: unknown ) {
      // Common failure: 403 when granter doesn't own the constellation
      // (DCM's `_require_constellation_owner` gate). FastAPI detail makes
      // this readable verbatim.
      toast.error( extractApiErrorMessage( err, 'Failed to connect constellation' ) );
    }
  } );

  const handleClose = () => {
    reset();
    setQuery( '' );
    onClose();
  };

  if ( !isOpen ) return null;

  const listboxId = `${ baseId }-listbox`;
  const optionIdFor = ( principal: string ) => `${ baseId }-opt-${ principal }`;

  const handleSearchKeyDown = ( e: React.KeyboardEvent<HTMLInputElement> ) => {
    if ( candidates.length === 0 ) return;
    const currentIdx = candidates.findIndex( ( c ) => c.principal === selectedPrincipal );
    if ( e.key === 'ArrowDown' ) {
      e.preventDefault();
      const next = currentIdx < 0 ? 0 : Math.min( currentIdx + 1, candidates.length - 1 );
      setValue( 'constellation', candidates[ next ].principal, { shouldValidate: true } );
    } else if ( e.key === 'ArrowUp' ) {
      e.preventDefault();
      const prev = currentIdx < 0 ? candidates.length - 1 : Math.max( currentIdx - 1, 0 );
      setValue( 'constellation', candidates[ prev ].principal, { shouldValidate: true } );
    }
  };

  const activeOptionId = selectedPrincipal ? optionIdFor( selectedPrincipal ) : undefined;

  return (
    <Modal modalType="add-constellation-modal" initialSize="medium">
      <form onSubmit={ onSubmit } className={ styles.modalForm }>
        <Heading as="h3" size="md">
          { contractName ? `Add Constellation to ${ contractName }` : 'Add Constellation' }
        </Heading>
        <Text>
          Connected constellations can view this contract, query its data, and
          cascade access to their agents.
        </Text>

        <Input
          id={ `${ baseId }-search` }
          label="Search Constellations by name"
          type="text"
          placeholder="e.g. process"
          value={ query }
          onChange={ ( e ) => setQuery( e.target.value ) }
          onKeyDown={ handleSearchKeyDown }
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-controls={ listboxId }
          aria-expanded={ candidates.length > 0 }
          aria-activedescendant={ activeOptionId }
        />

        <div
          className={ styles.candidateList }
          role="listbox"
          id={ listboxId }
          aria-label="Candidate constellations"
        >
          { agentsLoading && <div className={ styles.candidateEmpty }>Loading constellations…</div> }
          { /* Distinguish "fetch failed" from "no candidates". Without
               this branch, an auth-failed agents query falls through to
               agentsLoading=false + candidates=[] → user sees "No
               Constellations left to add" forever (misleading: implies
               nothing exists, but the real cause is no permission to
               list agents on aidp-core). */ }
          { !agentsLoading && agentsError && (
            <div
              className={ styles.candidateEmpty }
              data-testid="add-constellation-error"
            >
              Couldn&apos;t load constellations. You may not have permission
              to list constellations in this org, or aidp-core is unreachable.
            </div>
          ) }
          { !agentsLoading && !agentsError && candidates.length === 0 && (
            <div className={ styles.candidateEmpty }>
              { query ? 'No matching Constellations' : 'No Constellations left to add' }
            </div>
          ) }
          { candidates.map( ( { agent, principal } ) => (
            <button
              key={ principal }
              type="button"
              id={ optionIdFor( principal ) }
              role="option"
              aria-selected={ selectedPrincipal === principal }
              className={ clsx( styles.candidate, { [ styles.candidateSelected ]: selectedPrincipal === principal } ) }
              data-testid={ `add-constellation-candidate-${ principal }` }
              onClick={ () => setValue( 'constellation', principal, { shouldValidate: true } ) }
            >
              <span className={ styles.candidateName }>{ agentDisplayName( agent ) }</span>
            </button>
          ) ) }
        </div>
        { formState.errors.constellation && (
          <Text className={ styles.errorText }>{ formState.errors.constellation.message }</Text>
        ) }

        <div className={ styles.modalActions }>
          <Button variant="secondary" onClick={ handleClose } type="button">Cancel</Button>
          <Button
            variant="primary"
            type="submit"
            disabled={ !selectedPrincipal || grant.isPending }
            data-testid="add-constellation-submit"
          >
            Add Constellation
          </Button>
        </div>
      </form>
    </Modal>
  );
}
